/**
 * /ParallelDialer/client-portal: the agency's view of a client's portal
 * (tenant_admin only: App.tsx guard; the menu entry is filtered by role in
 * both shells since W1).
 *
 * W4 (B2-client-portal, dialer side):
 *   - the active customer's name, not the agency's email (quick win 12);
 *   - « Copier le lien » (CopyLink) to the client portal, /ClientDashboard;
 *   - « Voir comme le client »: a read-only preview that reuses the portal's
 *     own sections (src/components/backoffice/sections), fed with the
 *     customer's calls, without refactoring them (W5 unifies the portal).
 *     The preview is `inert`: nothing in it can be clicked or focused.
 * W5 (B2-client-portal, portal side): the preview opens on the portal's
 *   home (HomeSection), narrowed to the active customer: meetings to
 *   validate and the four figures with their trend, from the server
 *   aggregates (the dialer's capped call list would have made them wrong).
 *   REV-W5-5: without an active customer the preview says « choisissez un
 *   client » instead of showing empty figures and « aucun RDV à valider ».
 */
import { useState, type HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CopyLink } from '@/components/shared/CopyLink';
import { useDialerDerived } from '@/contexts/DialerDerivedContext';
import { useDialerData } from '@/contexts/DialerDataContext';
import { listCustomers } from '@/api/endpoints/customers';
import ClientPortalView from '@/components/dialer/ClientPortalView';
import HomeSection from '@/components/backoffice/sections/HomeSection';
import PerformanceSection from '@/components/backoffice/sections/PerformanceSection';
import CallsSection from '@/components/backoffice/sections/CallsSection';

export const CLIENT_PORTAL_PATH = '/ClientDashboard';
const PREVIEW_TABS = ['home', 'performance', 'calls'] as const;
type PreviewTab = (typeof PREVIEW_TABS)[number];

// React 18 has no typed `inert`; the attribute is passed as is.
const INERT = { inert: '' } as unknown as HTMLAttributes<HTMLDivElement>;

export default function ClientPortalScreen() {
  const { t } = useTranslation(['dialer']);
  // ADR-024, must be Customer-scoped (filteredAllCalls / filteredProspectQueue)
  // not tenant-wide. Otherwise switching the active Customer in the sidebar
  // does nothing visible here.
  const { filteredAllCalls, filteredProspectQueue, prospectsBySource } = useDialerDerived();
  const { selectedCustomerId } = useDialerData();
  const { data: customers = [] } = useQuery({
    queryKey: ['customers', { activeOnly: true }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: true, signal }),
    staleTime: 60_000,
  });
  const clientName = customers.find((c) => c.id === selectedCustomerId)?.company_name || null;
  const [preview, setPreview] = useState(false);
  const [tab, setTab] = useState<PreviewTab>('home');
  const portalUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${CLIENT_PORTAL_PATH}`;

  const actions = (
    <>
      <CopyLink url={portalUrl} label={t('dialer:clientPortalView.copyLink')} />
      <Button type="button" variant={preview ? 'default' : 'outline'} size="sm" aria-pressed={preview} onClick={() => setPreview((p) => !p)}>
        {preview ? <EyeOff className="mr-2 h-4 w-4" aria-hidden /> : <Eye className="mr-2 h-4 w-4" aria-hidden />}
        {t('dialer:clientPortalView.viewAsClient')}
      </Button>
    </>
  );

  return (
    <motion.div
      key="client-portal"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {preview && (
        <section className="rounded-xl border-2 border-dashed p-4" style={{ borderColor: 'var(--vf-accent)' }} aria-labelledby="portal-preview-h" data-testid="portal-preview">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 id="portal-preview-h" className="text-sm font-semibold text-foreground">
                {clientName ? t('dialer:clientPortalView.previewTitleFor', { name: clientName }) : t('dialer:clientPortalView.previewTitle')}
              </h2>
              <p className="text-xs text-muted-foreground">{t('dialer:clientPortalView.previewReadOnly')}</p>
            </div>
            {selectedCustomerId && (
              <div role="tablist" aria-label={t('dialer:clientPortalView.previewTabs')} className="flex gap-1">
                {PREVIEW_TABS.map((id) => (
                  <Button key={id} type="button" role="tab" aria-selected={tab === id} variant={tab === id ? 'secondary' : 'ghost'} size="sm" className="min-h-11" onClick={() => setTab(id)}>
                    {t(`dialer:clientPortalView.previewTab.${id}`)}
                  </Button>
                ))}
              </div>
            )}
          </div>
          {!selectedCustomerId ? (
            <p role="status" className="py-6 text-center text-sm text-muted-foreground" data-testid="portal-preview-choose">
              {t('dialer:clientPortalView.previewChooseCustomer')}
            </p>
          ) : (
            <div {...INERT} className="pointer-events-none select-none" data-testid="portal-preview-body">
              {tab === 'home'
                ? <HomeSection client={{ company_name: clientName }} calls={filteredAllCalls as never[]} customerId={selectedCustomerId} onTabChange={() => {}} />
                : tab === 'performance'
                  ? <PerformanceSection calls={filteredAllCalls as never[]} />
                  : <CallsSection calls={filteredAllCalls as never[]} />}
            </div>
          )}
        </section>
      )}
      <ClientPortalView
        clientName={clientName}
        actions={actions}
        allCalls={filteredAllCalls}
        prospectQueue={filteredProspectQueue}
        prospectsBySource={prospectsBySource}
      />
    </motion.div>
  );
}
